/**
 * Shared, persistent "selected community" state. Loaded once per session and kept
 * in localStorage so the choice survives navigation and reloads. The sidebar
 * selector (AdminSidebar) writes it; every community-scoped page reads it.
 *
 * It is now a VIEW OVER THE BOOTSTRAP (`@api/v1/me`, doc 04 §4.1) rather than a
 * fetch of its own. `GET /admin/communities` was the second leg of the 3-deep
 * preamble every page paid on a cold load; the community list, the account and the
 * sidebar badges all arrive in the one `GET /v1/me` now, and this module and
 * `lib/todos.svelte.ts` coordinate through that module's cache instead of through
 * each other.
 *
 * Two things changed behaviourally, both deliberate:
 *
 *  * THE SELECTION IS RESOLVED SERVER-SIDE. We no longer download the list in
 *    order to check that the id we already had is in it — we send it and the
 *    server confirms it or names the one it would have picked (`selected_skool_id`).
 *  * A PAGE NO LONGER HAS TO WAIT FOR ANY OF THIS. `withCommunity` issues the
 *    page's own fetch in the same round trip, against the id in localStorage, and
 *    retries against the resolved id only if the server says that id is stale.
 */
import { untrack } from 'svelte';

import type { Community } from './admin';
import { ApiError } from './admin';
import { fetchCommunities } from '@api';
import { isStaleCommunity } from '@api';
import { type Bootstrap, type MeCommunity } from '@api';
import { storeSkoolId, storedSkoolId } from './session';
import { ensureBootstrap, refreshBootstrap } from './session';

export const community = $state<{
  list: Community[];
  selectedId: number | null;
  loaded: boolean;
}>({ list: [], selectedId: null, loaded: false });

let loading: Promise<void> | null = null;

/** The one page size the fleet walk uses — `Pagination.max_limit` server-side, so
 *  a fleet of any realistic size costs one request. */
const FLEET_PAGE = 200;
/** Refuses to walk forever if a cursor ever stops advancing. 40 × 200 = 8,000
 *  communities, far past anything real; hitting it truncates rather than hangs. */
const FLEET_MAX_PAGES = 40;

/**
 * The community list to switch between.
 *
 * For a creator this is the bootstrap's held set, which is the whole story. For a
 * SUPERADMIN it is not: `held_communities()` returns the held set "even for a
 * superadmin" (`api/v1/common.py`) and a superadmin holds none — `skool_ids` is
 * empty by definition, because a superadmin is scoped by role instead. So their
 * bootstrap list is `[]`, and a `[]` here means an empty switcher, a null selection
 * and every community-scoped page with nothing to render.
 *
 * Before v1 this worked by accident: `GET /admin/communities` returned the whole
 * fleet to a superadmin on every page load, and the switcher read that. v1 split
 * the two jobs deliberately — the bootstrap stays small, and the fleet is
 * `GET /v1/communities` — but only the accounts-page dialog was moved onto the new
 * route. This is the other caller that split needed.
 *
 * Costs a superadmin one extra request on a cold load. It is not on the creator
 * path at all, which is the case the small bootstrap was for.
 */
async function switcherList(boot: Bootstrap): Promise<MeCommunity[]> {
  if (boot.user.role !== 'superadmin') return boot.communities;
  const out: MeCommunity[] = [];
  let cursor: string | null = null;
  for (let i = 0; i < FLEET_MAX_PAGES; i++) {
    const page = await fetchCommunities({ limit: FLEET_PAGE, cursor });
    out.push(...page.items);
    cursor = page.next_cursor;
    if (!cursor) break;
  }
  return out;
}

/** Adopt a bootstrap body. The server has already picked the selection; we only
 *  fall back to the first community for the case it cannot answer (an account that
 *  holds none, where `selected_skool_id` is null and so is the fallback).
 *
 *  `list` is passed in rather than read off `boot` because a superadmin's switcher
 *  is the fleet, not the (empty) held set — see `switcherList`. */
function adopt(boot: Bootstrap, list: MeCommunity[]): void {
  community.list = list;
  const resolved =
    boot.selected_skool_id != null && list.some((c) => c.id === boot.selected_skool_id)
      ? boot.selected_skool_id
      : (list[0]?.id ?? null);
  community.selectedId = resolved;
  community.loaded = true;
  // Write the server's answer back, so the NEXT cold load sends an id the server
  // will confirm — which is what keeps `withCommunity`'s optimistic first attempt
  // on the 1-RTT path instead of the 2-RTT recovery path.
  if (resolved != null && resolved !== storedSkoolId()) storeSkoolId(resolved);
}

/** Load the community list once and restore the persisted selection. Safe to call
 *  from every page; subsequent calls resolve immediately, and concurrent callers
 *  (this, the badges, the page itself) share the single `/v1/me`. */
export function ensureCommunities(): Promise<void> {
  if (community.loaded) return Promise.resolve();
  if (!loading) {
    loading = ensureBootstrap()
      .then(async (boot) => adopt(boot, await switcherList(boot)))
      .catch((e: unknown) => {
        loading = null; // allow retry after an auth/network failure
        throw e;
      });
  }
  return loading;
}

/**
 * Force-refetch the account and its communities — e.g. after a billing change
 * flips a skool's plan_status, so badges / the paywall reflect the new state
 * without a reload. Keeps the current selection if it still exists; falls back to
 * the first.
 *
 * This refreshes the whole bootstrap, not just the list: the two came back
 * together and a stale `plan_status` on the account with a fresh one on the
 * community is exactly the mismatch that sends a creator who has just paid back to
 * the trial page.
 */
export async function refreshCommunities(): Promise<void> {
  const boot = await refreshBootstrap(community.selectedId);
  adopt(boot, await switcherList(boot));
}

export function setCommunity(id: number): void {
  community.selectedId = id;
  storeSkoolId(id);
}

/**
 * The community the panel is currently scoped to, or null before the selection
 * resolves.
 *
 * Written once because it was written SEVEN times — the shell, the sidebar, the trial
 * chip, the roster chip, the pipeline board, the community picker and the category
 * table each carried their own
 * `community.list.find((c) => c.id === community.selectedId) ?? null`.
 *
 * Safe to call inside `$derived`/`$effect`: it reads `community.list` and
 * `community.selectedId` on every call, so the caller's dependency tracking sees both
 * exactly as it did when the expression was inline.
 */
export function activeCommunity(): Community | null {
  return community.list.find((c) => c.id === community.selectedId) ?? null;
}

/**
 * True when the active community has refused us its members page.
 *
 * `ask`/`escalate` both mean we asked and cannot read it. `unknown` deliberately says
 * nothing: a community no deep pass has checked has not refused us anything, and
 * accusing it would be a lie we would have to retract on the next tick.
 *
 * Two surfaces branch on this — the header's roster chip and the pipeline board's
 * empty columns — and they must agree, or a column explains itself with a prompt the
 * header is not offering.
 */
export function rosterMissing(): boolean {
  const state = activeCommunity()?.roster_state;
  return state === 'ask' || state === 'escalate';
}

/**
 * The community id to fetch with RIGHT NOW, before anything has resolved — the
 * live selection once the bootstrap has landed, and the localStorage id before it.
 *
 * This is what makes doc 04 §4.1 design point 2 possible: the id was always in the
 * browser, so there is nothing to wait for.
 */
export function optimisticSkoolId(): number | null {
  return community.loaded ? community.selectedId : storedSkoolId();
}

/**
 * Run a community-scoped fetch AT RTT 1, in parallel with the bootstrap.
 *
 *     const [boot, data] = await Promise.all([
 *       ensureBootstrap(),
 *       withCommunity((skoolId) => fetchInsights(skoolId, { last: '365d' })),
 *     ]);
 *
 * The fetch goes out immediately against the localStorage id. If that id turns out
 * to be stale the server answers `community_not_in_scope` (or `community_not_found`)
 * and this retries ONCE against the bootstrap's server-resolved `selected_skool_id`
 * — which has arrived by then, because it was issued in the same round trip. The
 * failure path costs 2 RTT; the path it replaces cost 3 on SUCCESS.
 *
 * Any other failure propagates untouched, so `handle401` still sees a 401/402
 * exactly as it does today.
 *
 * Throws when the account holds no community at all. A page that would rather
 * render an empty state than an error should check `community.selectedId` after
 * `await ensureCommunities()` instead of using this.
 */
export async function withCommunity<T>(fn: (skoolId: number) => Promise<T>): Promise<T> {
  if (!community.loaded) {
    // Kick the bootstrap off in the SAME round trip, whether or not the caller
    // remembered to. It is the same coalesced request either way, and it is what
    // guarantees the recovery below costs 2 RTT rather than 3 on a page that only
    // awaited this. The caller's own `ensureBootstrap()` owns any error; this
    // handler exists so a 401 here is not an unhandled rejection.
    void ensureCommunities().catch(() => {});
    const optimistic = storedSkoolId();
    if (optimistic != null) {
      try {
        return await fn(optimistic);
      } catch (err: unknown) {
        if (!isStaleCommunity(err)) throw err;
      }
    }
  }
  await ensureCommunities();
  const id = community.selectedId;
  if (id == null) throw new ApiError(0, 'This account has no community yet.');
  return fn(id);
}

// ---------------------------------------------------------------------------
// Reacting to a community switch
// ---------------------------------------------------------------------------

/**
 * Run `run(skoolId)` once per community, whenever the shared selection settles on a
 * new one. THE ONLY SUPPORTED WAY for a page to react to a community switch.
 *
 * WHY THIS EXISTS — the bug it makes unrepresentable
 * ---------------------------------------------------
 * `$effect` subscribes to what it ACTUALLY READ on a given run, not to what the
 * function mentions. Every page here had hand-rolled the same shape:
 *
 *     $effect(() => {
 *       if (loading || !community.loaded) return;   // <- returns BEFORE the read
 *       const id = community.selectedId;            // <- never reached, never tracked
 *       if (id == null || id === loadedFor) return;
 *       void load(id);
 *     });
 *
 * On any run that bails at line 1, `community.selectedId` is not a dependency. If
 * nothing else the effect read ever changes again, a later switch changes only
 * `selectedId` — and the effect never re-runs. The page then sits on whatever
 * community its `init()` happened to fetch while the sidebar and the selector both
 * move, which reads as "I changed the skool and the data didn't change".
 *
 * It bit the Pipelines board first because it bit a SUPERADMIN first. A creator's
 * community list arrives inside `GET /v1/me`, so `community.loaded` flips almost
 * immediately; a superadmin holds no `skool_ids`, so `held_communities()` returns
 * `[]` and the switcher needs the second `switcherList()` request before `loaded`
 * flips — a much wider window for the first run to bail in. The other pages survived
 * on luck: their extra `loading`/`ready` guard is `$state`, so flipping it gave the
 * effect a second run that did reach the read.
 *
 * So the rule is not "remember to read the store first". The rule is that no page
 * writes this effect at all:
 *
 *     onCommunityChange((id) => void load(id));
 *     onCommunityChange((id) => void load(id), { gate: () => !loading });
 *     onCommunityChange((id) => void load(id), { key: (id) => `${id}:${days}` });
 *
 * `scripts/check-community-effects.mjs` fails the build if `community.selectedId`
 * appears inside a `$effect` anywhere but here.
 *
 * WHAT IT GUARANTEES
 * ------------------
 *  * every reactive input — `loaded`, `selectedId`, `gate()`, `key()` — is read
 *    unconditionally, BEFORE any early return, so all four are always tracked;
 *  * `run` fires once per distinct key, so callers stop hand-rolling `loadedFor` /
 *    `lastId` / `loadedKey` and cannot get the dedupe subtly wrong;
 *  * `run` executes inside `untrack`, so whatever it reads does NOT become a
 *    dependency — several call sites had wrapped their own bodies to get this and
 *    the ones that had not were one stray read away from a re-entrant loop.
 *
 * Must be called during component initialisation, like `$effect` itself.
 *
 * @param run   what to do with the newly selected community.
 * @param gate  optional precondition (e.g. `() => !loading`). Read every run, so it
 *              may depend on reactive state; `run` fires on the first run where it
 *              is true.
 * @param key   optional identity for the dedupe, when a reload depends on more than
 *              the community (e.g. a selected day range). Receives the id, which is
 *              null before the selection resolves. Read every run — so read any
 *              reactive state it needs UNCONDITIONALLY inside it.
 *
 * @returns a {@link CommunityWatch} — the two escape hatches the hand-rolled
 *          versions needed, and nothing else.
 */
export interface CommunityWatch {
  /**
   * Forget the last key, so the next run repeats work already done for the current
   * community. This is what the pages that nulled their own `loadedFor` /
   * `loadedKey` in a `catch` were doing: a FAILED load must not be remembered as
   * done, or the retry never comes. Call it from the error path, exactly there.
   */
  rearm(): void;
  /**
   * Record a key as already handled by work done OUTSIDE the watcher — specifically
   * a page's `init()`, which fetches at RTT 1 against `optimisticSkoolId()` rather
   * than waiting for the bootstrap (doc 04 §4.1 design point 2). Without this the
   * watcher would fire for that same community the moment the selection settles and
   * every cold load would fetch twice.
   */
  seen(key: string | number | null): void;
}

export function onCommunityChange(
  run: (skoolId: number) => void,
  opts: {
    gate?: () => boolean;
    key?: (skoolId: number | null) => string | number | null;
  } = {},
): CommunityWatch {
  let last: string | number | null = null;
  $effect(() => {
    // Every reactive read is unconditional and happens before any `return` — this
    // ordering IS the fix, and it is why call sites must not re-implement it.
    const loaded = community.loaded;
    const id = community.selectedId;
    const open = opts.gate ? opts.gate() : true;
    const k = opts.key ? opts.key(id) : id;
    if (!loaded || !open || id == null || k == null || k === last) return;
    last = k;
    untrack(() => run(id));
  });
  return {
    rearm: () => {
      last = null;
    },
    seen: (key) => {
      last = key;
    },
  };
}
